import { useEffect, useMemo, useRef, useState } from 'react';

import { useFavorites } from '../lib/favorites.js';
import { PIE_ANGLES, closePie, getPie, pieSlotAt, pointer, usePieMenu, usePieSettings, type PieItem } from '../lib/pies.js';
import { keyHint, runCommand, shortcuts, useShortcutVersion } from '../lib/shortcuts/index.js';
import { Card, ContextMenu, NumberField, PropRow, type MenuItem } from './ui/controls.js';
import { Icon, type IconName } from './ui/icons.js';


function runItem(item: PieItem) {
  if (item.disabled) return;
  if (item.commandId) runCommand(item.commandId);
  else item.run?.();
}

/** Renders the open pie menu, if any. Mounted once by the Workspace. */
export function PieMenuHost() {
  const open = usePieMenu((s) => s.open);
  useShortcutVersion();
  const def = open ? getPie(open.id) : undefined;
  const items = useMemo(() => (def ? def.items().slice(0, PIE_ANGLES.length) : []), [def, open]);
  const { radius, threshold, tapMs, confirm } = usePieSettings();
  const [hover, setHover] = useState(-1);
  const hoverRef = useRef(-1);
  hoverRef.current = hover;

  useEffect(() => {
    if (!open) return;
    setHover(-1);
    const finish = (index: number) => {
      closePie();
      const item = items[index];
      if (item) runItem(item);
    };
    const move = (e: PointerEvent) => {
      const dx = e.clientX - open.x;
      const dy = e.clientY - open.y;
      const slot = pieSlotAt(dx, dy, items.length, threshold);
      // Blender's "confirm" distance: travelling this far runs the item outright.
      if (confirm > 0 && slot >= 0 && Math.hypot(dx, dy) >= confirm) finish(slot);
      else setHover(slot);
    };
    const keyup = (e: KeyboardEvent) => {
      if (['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) return;
      // Held and flicked: run what the mouse points at. Tapped: stay open.
      if (performance.now() - open.at > tapMs && hoverRef.current >= 0) finish(hoverRef.current);
    };
    const keydown = (e: KeyboardEvent) => {
      if (e.repeat) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closePie();
      } else if (/^[1-8]$/.test(e.key) && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        e.stopImmediatePropagation();
        finish(Number(e.key) - 1);
      }
    };
    const down = (e: PointerEvent) => {
      if (e.button === 2) {
        closePie();
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target?.closest('.pie-item')) return;
      // A click in the middle cancels; elsewhere picks the pointed-at slot.
      const slot = pieSlotAt(e.clientX - open.x, e.clientY - open.y, items.length, threshold);
      e.preventDefault();
      if (slot >= 0) finish(slot);
      else closePie();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('keyup', keyup, true);
    window.addEventListener('keydown', keydown, true);
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('blur', closePie);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('keyup', keyup, true);
      window.removeEventListener('keydown', keydown, true);
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('blur', closePie);
    };
  }, [open, items, threshold, tapMs, confirm]);

  if (!open || !def) return null;
  const angle = hover >= 0 ? PIE_ANGLES[hover]! : null;

  return (
    <div className="pie-layer" role="menu" aria-label={def.label}>
      <div className="pie" style={{ left: open.x, top: open.y }}>
        <div className="pie-center">
          {angle !== null && <div className="pie-pointer" style={{ transform: `rotate(${-angle}deg)` }} />}
        </div>
        <div className="pie-title">{def.label}</div>
        {items.map((item, i) => {
          const a = (PIE_ANGLES[i]! * Math.PI) / 180;
          const x = Math.cos(a) * radius * (i >= 4 ? 1.05 : 1.25);
          const y = -Math.sin(a) * radius * (i >= 4 ? 1.3 : 1);
          const hint = item.commandId ? keyHint(item.commandId) : '';
          return (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={`pie-item${hover === i ? ' is-hover' : ''}${item.active ? ' is-active' : ''}`}
              style={{ transform: `translate(calc(${x}px - 50%), calc(${y}px - 50%))` }}
              onPointerEnter={() => setHover(i)}
              onClick={() => {
                closePie();
                runItem(item);
              }}
            >
              <span className="pie-num">{i + 1}</span>
              {item.icon && <Icon name={item.icon as IconName} size={14} />}
              <span>{item.label}</span>
              {hint && <kbd className="kbd">{hint}</kbd>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The Quick Favorites menu (Q), shown under the mouse. */
export function QuickFavoritesHost({ onManage }: { onManage: () => void }) {
  const menu = useFavorites((s) => s.menu);
  const ids = useFavorites((s) => s.ids);
  const close = useFavorites((s) => s.close);
  useShortcutVersion();
  if (!menu) return null;
  const items: MenuItem[] = [{ id: 'h', label: 'Quick Favorites', heading: true }];
  for (const id of ids) {
    const command = shortcuts.get(id);
    if (!command || command.external) continue;
    items.push({
      id,
      label: command.label,
      icon: command.icon as IconName | undefined,
      shortcut: keyHint(id) || undefined,
      disabled: command.enabled ? !command.enabled() : false,
      run: () => runCommand(id),
    });
  }
  if (items.length === 1) items.push({ id: 'empty', label: 'No favorites yet', disabled: true });
  items.push({ id: 'sep', label: '', separator: true }, { id: 'manage', label: 'Add or remove favorites…', icon: 'search', run: onManage });
  return <ContextMenu x={menu.x} y={menu.y} items={items} onClose={close} />;
}

export function openFavoritesAtPointer(): void {
  useFavorites.getState().open({ x: pointer.x || window.innerWidth / 2, y: pointer.y || window.innerHeight / 3 });
}

/** Settings › Pie Menus. */
export function PieSettings() {
  const settings = usePieSettings();
  return (
    <div className="layout-settings">
      <h3>Pie menus</h3>
      <p className="muted">
        Hold a pie key and flick toward an item, then release to run it; tap the key to keep the pie open and click. These
        follow Blender&rsquo;s pie preferences.
      </p>
      <Card title="Behaviour" icon="layers">
        <PropRow label="Radius" hint="Distance of the items from the centre, in pixels">
          <NumberField value={settings.radius} integer min={60} max={240} step={4} onChange={(v) => settings.set({ radius: v })} />
        </PropRow>
        <PropRow label="Threshold" hint="Dead zone around the centre before a direction is chosen, in pixels">
          <NumberField value={settings.threshold} integer min={4} max={80} step={1} onChange={(v) => settings.set({ threshold: v })} />
        </PropRow>
        <PropRow label="Tap time" hint="Releasing the key sooner than this (ms) keeps the pie open">
          <NumberField value={settings.tapMs} integer min={50} max={1000} step={10} onChange={(v) => settings.set({ tapMs: v })} />
        </PropRow>
        <PropRow label="Confirm distance" hint="Moving this far (px) runs the item at once; 0 turns it off">
          <NumberField value={settings.confirm} integer min={0} max={400} step={10} onChange={(v) => settings.set({ confirm: v })} />
        </PropRow>
        <button type="button" className="ghost small" onClick={settings.reset}>
          Restore defaults
        </button>
      </Card>
    </div>
  );
}
